import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function SimpleBarChart({
  data = [],
  max = 100,
  height = 150,
  xLabel,
  yLabel,
  ticks = [],
  valueInside = false,
  valueClass = 'text-lg',
  labelFontSize = 12,
  labelBold = false,
  showValueLabels = true,
}) {
  const labels = data.map(d => d.label);
  const values = data.map(d => d.value);
  const colors = data.map(d => d.color);
  const displayVals = data.map(d => d.display || d.value);
  const tooltipVals = data.map(d => d.tooltip || null);
  const insideFlags = data.map(d =>
    typeof d.valueInside === 'boolean' ? d.valueInside : valueInside
  );
  const chartData = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: colors,
        borderRadius: 4,
        displayVals,
        tooltipVals,
        valueInsideFlags: insideFlags,
        valueFontClass: valueClass,
        showValueLabels,
      },
    ],
  };

  const valuePlugin = {
    id: 'valuePlugin',
    afterDatasetsDraw(chart) {
      const dataset = chart.data.datasets?.[0];
      if (!dataset) {
        return;
      }
      const shouldShow = dataset.showValueLabels !== false;
      if (!shouldShow) {
        return;
      }
      const { ctx } = chart;
      const meta = chart.getDatasetMeta(0);
      const vals = dataset.displayVals || [];
      const flags = dataset.valueInsideFlags || [];
      const fontClass = dataset.valueFontClass || 'text-lg';
      meta.data.forEach((bar, index) => {
        const val = vals[index];
        ctx.save();
        ctx.fillStyle = 'white';
        const size = fontClass.includes('text-xs')
          ? 10
          : fontClass.includes('text-sm')
          ? 14
          : fontClass.includes('text-lg')
          ? 18
          : 12;
        const weight = fontClass.includes('font-bold') ? 'bold' : 'normal';
        ctx.font = `${weight} ${size}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const x = bar.x;
        const y = flags[index]
          ? bar.y + (bar.base - bar.y) / 2
          : bar.y - 6;
        ctx.fillText(val, x, y);
        ctx.restore();
      });
    },
  };

  const allTicks = ticks.length ? ticks : Array.from({ length: 6 }, (_, i) => (max / 5) * i);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          label: ctx => {
            const tip = ctx.dataset.tooltipVals?.[ctx.dataIndex];
            const value = ctx.dataset.displayVals?.[ctx.dataIndex] ?? ctx.formattedValue;
            if (tip == null) {
              return value;
            }
            if (typeof tip === 'string') {
              return `${tip}: ${value}`;
            }
            if (Array.isArray(tip)) {
              return tip;
            }
            if (typeof tip === 'object') {
              if (tip.label) {
                return `${tip.label}: ${tip.value ?? value}`;
              }
              return Object.values(tip)
                .map(v => (v === undefined ? value : v))
                .join(': ');
            }
            return `${tip}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: !!xLabel, text: xLabel },
        grid: { display: false },
        ticks: {
          font: { size: 12, weight: 'normal' },
        },
      },
      y: {
        suggestedMax: max,
        ticks: { callback: v => v, values: allTicks },
        title: { display: !!yLabel, text: yLabel },
        beginAtZero: true,
      },
    },
  };

  return (
    <div
      className={`relative ${yLabel ? 'pl-8' : ''} w-full h-full flex-1`}
      style={{ height }}
    >
      <Bar data={chartData} options={options} plugins={[valuePlugin]} />
    </div>
  );
}

export default SimpleBarChart;
